import { useEffect, useState } from 'react';

import { computeBoletoCutoff, isBoletoLocked } from '../utils/boletoCutoff';

// setTimeout estoura acima de 2^31 - 1 ms (~24,8 dias).
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

/**
 * FIN-8268: `true` quando o boleto está travado (passou do corte e a oferta
 * ainda vende). Falso no SSR e sem `boletoCutoffDays`/`availableUntil`.
 * Reavalia sozinho ao cruzar o corte com a página aberta.
 */
export function useBoletoLock(
  availableUntil: string | null | undefined,
  boletoCutoffDays: number | null | undefined
): boolean {
  const [isLocked, setIsLocked] = useState(false);

  useEffect(() => {
    const cutoff = computeBoletoCutoff(availableUntil, boletoCutoffDays);

    if (!cutoff) {
      setIsLocked(false);
      return undefined;
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const evaluate = () => {
      const nowMs = Date.now();
      setIsLocked(isBoletoLocked({ availableUntil, boletoCutoffDays, nowMs }));

      const untilMs = Date.parse(availableUntil as string);
      // Próximo instante de interesse: o corte (trava) ou o fim das vendas.
      const nextMs = [cutoff.getTime(), untilMs].find((ms) => ms > nowMs);

      if (nextMs !== undefined) {
        timeoutId = setTimeout(
          evaluate,
          Math.min(nextMs - nowMs + 1, MAX_TIMEOUT_MS)
        );
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (timeoutId !== undefined) clearTimeout(timeoutId);
        evaluate();
      }
    };

    evaluate();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [availableUntil, boletoCutoffDays]);

  return isLocked;
}
